import { useGetAllClientMarketItchConnectionsInfinite } from '@/hooks/query/useGetAllClientMarketItchConnectionsInfinite.ts'
import { useNavigate } from '@tanstack/react-router'
import { ChevronRightIcon } from 'lucide-react'

import { useGetClientMarket } from '@/gen/publicApi'

import { UnexpectedDataStateError } from '@/containers/unexpectedDataStateError'

import { Card } from '@/components/card'
import LoadMoreButton from '@/components/loadMoreButton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/table'

import { useSetBreadcrumbs } from '@/state/breadcrumbs'

import { cn } from '@/utils/cn'

interface FixConnectionsTableProps {
    market_code: string
    client_code: string
}

export function ItchConnectionsTable({ market_code, client_code }: FixConnectionsTableProps) {
    const { data: market } = useGetClientMarket(client_code, market_code, { query: { suspense: true } })
    const {
        data: marketItchConnections,
        fetchNextPage,
        hasNextPage,
        isLoading,
    } = useGetAllClientMarketItchConnectionsInfinite(client_code, market_code, false)

    const navigate = useNavigate()
    useSetBreadcrumbs([
        {
            to: '/$client_code/markets',
            text: 'Markets',
            params: {
                client_code,
            },
        },
        'seperator',
        {
            to: '/$client_code/markets/$market_code',
            params: {
                market_code,
                client_code,
            },
            text: market?.name || '--',
        },
        'seperator',
        {
            to: '/$client_code/markets/$market_code/connections',
            params: {
                market_code,
                client_code,
            },
            text: 'Connections',
        },
    ])

    const onRowClick = (connectionId: string) => {
        navigate({
            to: `/$client_code/markets/$market_code/connections/itch/$connection_id`,
            params: {
                client_code,
                market_code,
                connection_id: connectionId,
            },
        })
    }

    if (!marketItchConnections) {
        return <UnexpectedDataStateError />
    }

    return (
        <Card className="flex-grow" hideOverflow>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>Dialect</TableHead>
                        <TableHead>Username</TableHead>
                        <TableHead />
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {marketItchConnections.map(connection => (
                        <TableRow
                            key={connection.id}
                            onClick={() => onRowClick(connection.id)}
                            className={cn('cursor-pointer', {
                                'text-destructive': connection.rowState === 'Deleted',
                            })}
                        >
                            <TableCell>{connection.dialect}</TableCell>
                            <TableCell>{connection.username}</TableCell>
                            <TableCell className="flex justify-end">
                                <ChevronRightIcon />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <div className="flex justify-center">
                <LoadMoreButton
                    className="my-4"
                    fetchNextPage={fetchNextPage}
                    isLoading={isLoading}
                    hasNextPage={hasNextPage}
                />
            </div>
        </Card>
    )
}
